/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-restricted-syntax */
/* eslint-disable guard-for-in */
import { isEmpty } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { capitalizeFirst, fetchWithHeaders, urls } from '@sendible/common';
import { useToastNotificationContext } from '@sendible/design-system';
import { NotificationTypeEnum } from '@sendible/design-system/src/components/molecules/Toast';
import { useTranslation } from 'react-i18next';
import { useBridgeContext } from '@sendible/shared-state-bridge';
import { FeedbackMessage } from '@sendible/design-system/src/components/molecules/ComposeDropDown';
import { validationErrorSorted } from './utils/consts';
import endpoints from '../../data-layer/endpoints';
import { useMediaLibraryContext } from '../../pages/MediaLibrary/context';
import { useInfiniteQueryWithAccessToken, useMutationWithAccessToken, useQueryWithAccessToken } from '../../data-layer/useReactQueryWithAccessToken';
import { mediaRenameFromPreview } from '../../pages/MediaLibrary/pendoEvents';
import { composeMediaInteractions } from '../../components/MediaSystem/composeMediaInteractions';
import { useBackgroundUploaderContext } from '../../components/BackgroundUploader/context';

export const useGetMediasQueryKey = (): DLInfiniteQueryKey => {
  const { activeMediaLibrary, mediaSearch, perPage } = useMediaLibraryContext();
  const paramObj = {
    mediaLibraryId: activeMediaLibrary?.id,
    name: mediaSearch,
    status: 'Ready,Uploaded,Processing,New',
    perPage,
  };

  return [endpoints.GetMedias.endpoint, paramObj];
};
// polls the list of attached media items
export const useGetMediasForComposeBox = (): MediaType[] | undefined => {
  const [
    {
      compose: { tabsState, activeComposeBoxTab },
    },
  ] = useBridgeContext();
  const { updateFileReady, updateFileErrorByMediaId } = useBackgroundUploaderContext();
  const { t } = useTranslation('compose_box');

  const mediasForActiveTab = tabsState[activeComposeBoxTab]?.mediaAttached;

  const useGetAttachedMediasData = () =>
    useQueryWithAccessToken<MediaType[]>([endpoints.GetMedia.endpoint, { ids: Object.keys(mediasForActiveTab || {}) }], {
      select: (medias: MediaType[]) => {
        if (medias.length) {
          const keyMapping: Record<string, string> = {
            facebook: 'facebook',
            googleBusiness: 'googlemybusinesslocation',
            instagram: 'instagram',
            linkedIn: 'linkedin',
            other: 'other',
            pinterest: 'pinterest',
            profileList: 'group',
            tikTok: 'tiktok',
            twitter: 'twitter',
            wordPress: 'other',
            youTube: 'other',
          };

          // Format API validations to FE expectations
          const formattedValidations = medias.map(({ validations }): Record<string, any> => {
            const tempObj: Record<string, string> = {};

            for (const key in validations) {
              const newKey = keyMapping[key] || key;
              const capitalizedKeys: any = {};

              for (const k in validations[key]) {
                const capitalizedKey = capitalizeFirst(k);

                capitalizedKeys[capitalizedKey] = validations[key][k];
              }

              tempObj[newKey] = capitalizedKeys;
            }

            return tempObj;
          });

          // Create the most important validation alert/warning message for each media based in the tab
          const validationErrors = formattedValidations.map((validations, i): FeedbackMessage | undefined => {
            if (isEmpty(validations) || activeComposeBoxTab === 'message') return undefined;

            const validationCodes = validations[activeComposeBoxTab][medias[i].type] || undefined;

            if (validationCodes) {
              const validationMessage = validationErrorSorted.find(
                ({ message: msg, forTabs }) => validationCodes.includes(msg) && forTabs.includes(activeComposeBoxTab)
              );

              if (validationMessage) {
                return {
                  message: t(validationMessage.message),
                  type: validationMessage.type,
                };
              }
            }

            return undefined;
          });

          medias.forEach((_, index) => {
            medias[index].validations = formattedValidations[index];
            medias[index].validationError = validationErrors ? validationErrors[index] : undefined;
          });
        }

        return medias as MediaType[];
      },
      refetchInterval: endpoints.GetMedia.interval,
      enabled: !!Object.keys(mediasForActiveTab || {}).length && Object.values(mediasForActiveTab || {}).some((media) => media.status !== 'Ready'),
    });

  const { data } = useGetAttachedMediasData();

  useEffect(() => {
    data?.forEach((item) => {
      if (item.status === 'UploadFailed') {
        composeMediaInteractions.removeMedia(item.id.toString());
        updateFileErrorByMediaId(item.id.toString(), item.upload?.error?.code || 'UnknownError');
      } else if (item.status === 'Ready') {
        updateFileReady(item.id.toString());
      }
    });
    composeMediaInteractions.updateMediaProperties(data?.filter((media) => media.status !== 'UploadFailed') || []);
  }, [data]);

  return Object.values(mediasForActiveTab || {});
};

export const useInfiniteGetMedias = () => {
  const { activeMediaLibrary } = useMediaLibraryContext();
  const mediasQueryKey = useGetMediasQueryKey();

  return useInfiniteQueryWithAccessToken<MediaType[]>(mediasQueryKey, {
    enabled: !!activeMediaLibrary?.id,
    refetchInterval: endpoints.GetMedias.interval,
  });
};

export const useDeleteMedia = (pendoEvent: string, mediaType?: string) => {
  const queryClient = useQueryClient();
  const mediasQueryKey = useGetMediasQueryKey();
  const { selectedMediasIds, setSelectedMediasIds } = useMediaLibraryContext();
  const { t } = useTranslation('media_library');
  const { method, endpoint } = endpoints.DeleteMedia;
  const { showToastNotification } = useToastNotificationContext();

  return useMutationWithAccessToken<MediaType, MediaMutationParamsType>({
    method,
    endpoint,
    options: {
      onSuccess: useCallback(
        ({ mediaId, name }) => {
          showToastNotification({
            notificationText: t('toast_single_media_deletion_success', { mediaName: name }) || '',
            type: NotificationTypeEnum.Success,
            showCloseButton: true,
          });

          window.pendo.track(pendoEvent, {
            name,
            type: mediaType,
          });
          queryClient.invalidateQueries(mediasQueryKey);

          // If the media is selected, deselect it
          if (selectedMediasIds.includes(mediaId)) {
            const newList = [...selectedMediasIds];

            newList.splice(newList.indexOf(mediaId), 1);
            setSelectedMediasIds(newList);
          }
        },
        [selectedMediasIds]
      ),
      onError: useCallback((_, { name }) => {
        showToastNotification({
          notificationText: t('toast_medias_deletion_error', { name }) || '',
          type: NotificationTypeEnum.Error,
          showCloseButton: true,
        });
      }, []),
    },
  });
};

export const useDeleteMultipleMedias = (pendoEvent: string) => {
  const queryClient = useQueryClient();
  const mediasQueryKey = useGetMediasQueryKey();
  const { t } = useTranslation('media_library');
  const { showToastNotification } = useToastNotificationContext();

  const { method, endpoint } = endpoints.DeleteMedia;

  return useMutationWithAccessToken<MediaType[], { selectedMediasIds: string[]; access_token: string }>({
    method,
    endpoint,
    options: {
      onSuccess: (selectedMediasIds) => {
        queryClient.invalidateQueries(mediasQueryKey);

        showToastNotification({
          notificationText: t('toast_multiple_medias_deletion_success', { numberOfMedias: selectedMediasIds.length }) || '',
          type: NotificationTypeEnum.Success,
          showCloseButton: true,
        });

        window.pendo.track(pendoEvent, { selectedMediasIds });
      },
      onError: () => {
        showToastNotification({
          notificationText: t('toast_multiple_medias_deletion_error') || '',
          type: NotificationTypeEnum.Error,
          showCloseButton: true,
        });
      },
    },
    mutationFn: async ({ selectedMediasIds, access_token }) => {
      const responses: PromiseSettledResult<MediaType>[] = await Promise.allSettled(
        selectedMediasIds.map((mediaId) =>
          fetchWithHeaders({
            method,
            url: `${urls.baseUrl}${endpoint}`,
            params: { mediaId, access_token },
          }).then(({ result }) => result)
        )
      );

      const rejectedResponses = responses.filter((response) => response.status === 'rejected');

      if (rejectedResponses.length > 0) {
        throw new Error();
      }

      return responses.filter((response) => response.status === 'fulfilled').map((response) => (response as PromiseFulfilledResult<MediaType>).value);
    },
  });
};

export const useRenameMedia = (mediaType: string, newName: string) => {
  const { t } = useTranslation('media_library');
  const queryClient = useQueryClient();
  const mediasQueryKey = useGetMediasQueryKey();
  const { showToastNotification } = useToastNotificationContext();

  return useMutationWithAccessToken<MediaType, MediaMutationParamsType>({
    method: endpoints.RenameMedia.method,
    endpoint: endpoints.RenameMedia.endpoint,
    body: { name: newName },
    options: {
      onSuccess: useCallback(({ name }) => {
        showToastNotification({
          notificationText: t('toast_media_rename_success') || '',
          type: NotificationTypeEnum.Success,
          showCloseButton: true,
        });

        window.pendo.track(mediaRenameFromPreview, {
          name,
          type: mediaType,
        });
        queryClient.invalidateQueries(mediasQueryKey);
      }, []),
      onError: useCallback(() => {
        showToastNotification({
          notificationText: t('toast_media_rename_error') || '',
          type: NotificationTypeEnum.Error,
          showCloseButton: true,
        });
      }, []),
    },
  });
};
